import * as React from 'react';
import { FormControlLabel, FormGroup, Radio } from '@mui/material';
import { DateRangeSchema, useCustomSearchParams } from 'common/hooks/customSearchParams';
import { DEFAULT_DATE_RANGE_PARAM, WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import _ from 'lodash';
import moment from 'moment';

import { FilterButton } from '../../../../../components/ContainerFilters/FilterButton';
import { DateInput } from '../../../../../components/ContainerFilters/DateInput';
import { GatedOutDateRange, GatedOutFilterKind, getGatedOutFilters } from './parsers/gated-out';

interface State {
  moreThanFive: boolean;
  lessThanFive: boolean;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

export const GatedOutFilter: React.FC = () => {
  const controlLabelClasses = {
    label: 'font-medium text-sm py-1',
    root: 'm-0 hover:bg-blue-200 rounded-sm',
  };
  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS, true);
  const [filterStates, setFilterStates] = React.useState<State>({
    moreThanFive: false,
    lessThanFive: false,
    startDate: null,
    endDate: null,
  });

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const filters = getGatedOutFilters(searchParams);

    const rangeFilter = filters.find((f) => f.kind === GatedOutFilterKind.DateRange) as
      | GatedOutDateRange
      | undefined;

    setFilterStates({
      moreThanFive: !!filters.find((f) => f.kind === GatedOutFilterKind.MoreThanFive),
      lessThanFive: !!filters.find((f) => f.kind === GatedOutFilterKind.LessThanFive),
      startDate: rangeFilter ? rangeFilter.startDate : null,
      endDate: rangeFilter ? rangeFilter.endDate : null,
    });
  }, [searchParams]);

  const handleChange = (key: string, checked: boolean) => {
    const newFilters = {
      moreThanFive: false,
      lessThanFive: false,
      [key]: checked,
    };

    const defaultSearchParams = {
      containerStatus: _.uniq(_.concat(searchParams.containerStatus, 'gated_out')),
    };

    if (newFilters.moreThanFive) {
      setSearchParams({
        ...defaultSearchParams,
        gatedOutAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          endDate: '-5:days',
        },
      });
    } else if (newFilters.lessThanFive) {
      setSearchParams({
        ...defaultSearchParams,
        gatedOutAt: {
          ...DEFAULT_DATE_RANGE_PARAM,
          startDate: '-5:days',
        },
      });
    }

    setOpen(false);
  };

  const handleDateChange = (date: moment.Moment | null, kind: 'start' | 'end'): boolean => {
    let { startDate, endDate } = filterStates;

    switch (kind) {
      case 'start':
        if (date && endDate && date.isAfter(endDate)) {
          endDate = null;
        }
        startDate = date;
        break;
      case 'end':
        if (date && startDate && date.isBefore(startDate)) {
          startDate = null;
        }
        endDate = date;
        break;
      default:
        break;
    }
    const gatedOutAt: DateRangeSchema = { startDate, endDate };

    setSearchParams({ gatedOutAt });

    return true;
  };

  const clearFilters = () => {
    setSearchParams({
      gatedOutAt: null,
    });
  };

  return (
    <FilterButton
      title="Gated out"
      count={_.keys(_.pickBy(filterStates)).length}
      clearFilter={clearFilters}
      isOpen={open}
      onRequestOpen={setOpen}
    >
      <FormGroup className="px-2">
        <FormControlLabel
          classes={controlLabelClasses}
          control={
            <Radio
              size="small"
              checked={filterStates.moreThanFive}
              onChange={(e) => handleChange('moreThanFive', e.target.checked)}
            />
          }
          label="Gated out 5+ days"
        />
        <FormControlLabel
          classes={controlLabelClasses}
          control={
            <Radio
              size="small"
              checked={filterStates.lessThanFive}
              onChange={(e) => handleChange('lessThanFive', e.target.checked)}
            />
          }
          label="Not gated out 5+ days"
        />
        <DateInput
          label="On / after"
          date={filterStates.startDate}
          onChange={(date) => handleDateChange(date, 'start')}
          checked={!!filterStates.startDate}
          size="small"
        />
        <DateInput
          label="On / before"
          date={filterStates.endDate}
          onChange={(date) => handleDateChange(date, 'end')}
          checked={!!filterStates.endDate}
          size="small"
        />
      </FormGroup>
    </FilterButton>
  );
};
GatedOutFilter.displayName = 'TrackAndTrace.Controls.filters.GatedOutFilter';
